/**
 * Modal de reconnexion des identifiants ANEF
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import { clientAuthApi } from '../../services/clientApi';

interface ReconnectAnefModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  anefIdentifiant?: string;
}

export default function ReconnectAnefModal({
  open,
  onClose,
  onSuccess,
  anefIdentifiant,
}: ReconnectAnefModalProps) {
  const [anefPassword, setAnefPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!anefPassword) {
      setError('Veuillez entrer votre mot de passe ANEF');
      return;
    }

    try {
      setLoading(true);

      const response = await clientAuthApi.reconnectAnef(anefPassword);

      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess();
          onClose();
          // Réinitialiser le formulaire
          setAnefPassword('');
          setSuccess(false);
        }, 2000);
      }
    } catch (err: any) {
      console.error('Erreur reconnexion ANEF:', err);
      setError(
        err.response?.data?.message ||
        'Impossible de se connecter à ANEF. Vérifiez votre mot de passe.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setAnefPassword('');
      setError('');
      setSuccess(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            <Typography variant="h6" component="div">
              Reconnexion ANEF requise
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Votre mot de passe ANEF a changé</strong>
            </Typography>
            <Typography variant="caption">
              Pour continuer à synchroniser vos dossiers, veuillez entrer votre nouveau mot de passe ANEF.
            </Typography>
          </Alert>

          {anefIdentifiant && (
            <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Identifiant ANEF
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {anefIdentifiant}
              </Typography>
            </Box>
          )}

          <TextField
            fullWidth
            label="Mot de passe ANEF"
            type={showPassword ? 'text' : 'password'}
            value={anefPassword}
            onChange={(e) => setAnefPassword(e.target.value)}
            disabled={loading || success}
            autoFocus
            required
            error={!!error && !success}
            helperText={error || 'Entrez votre mot de passe ANEF actuel'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading || success}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {success && (
            <Alert
              severity="success"
              icon={<CheckCircle />}
              sx={{ mt: 2 }}
            >
              <Typography variant="body2">
                ✅ Identifiants ANEF mis à jour avec succès !
              </Typography>
              <Typography variant="caption">
                Redirection en cours...
              </Typography>
            </Alert>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            disabled={loading || success}
            color="inherit"
          >
            Annuler
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success || !anefPassword}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Vérification...' : success ? 'Connexion réussie' : 'Reconnecter'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
